// NotificationBell — header bell icon + dropdown preview + 20s poll.
// Renders unread count badge, last 8 notifications, mark-as-read on click,
// and a "View all" link to the dedicated /notifications page.

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20_000;
const PREVIEW_LIMIT = 8;

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inflightRef = useRef(false);

  const fetchCount = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const r = await fetch(`${BACKEND_URL}/api/notifications/unread-count`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setUnread(d.count || 0);
      }
    } catch (_) { /* ignore network blips */ }
    finally { inflightRef.current = false; }
  }, []);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/notifications?limit=${PREVIEW_LIMIT}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setItems(d.notifications || []);
        setUnread(d.total_unread != null ? d.total_unread : unread);
      }
    } catch (_) {}
    finally { setLoading(false); }
  }, [unread]);

  // Poll the cheap unread-count every 20s. Refresh when tab regains focus.
  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [fetchCount]);

  // Refresh full list every time the dropdown opens
  useEffect(() => { if (open) fetchPreview(); }, [open, fetchPreview]);

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      // Optimistic: mark locally, fire request
      setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
      fetch(`${BACKEND_URL}/api/notifications/${n.notification_id}/read`, { method: "PATCH", credentials: "include" }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAllRead = async (e) => {
    e?.stopPropagation();
    setUnread(0);
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    try {
      await fetch(`${BACKEND_URL}/api/notifications/mark-all-read`, { method: "POST", credentials: "include" });
    } catch (_) {}
  };

  const typeAccent = (type) => {
    if (type === "new_lead") return "bg-emerald-500";
    if (type === "task_approval_required" || type === "task_assigned") return "bg-violet-500";
    if (type === "follow_up_reminder") return "bg-amber-500";
    return "bg-slate-400";
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative flex items-center justify-center w-10 h-10 mr-1 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        >
          <Bell className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-800"
              data-testid="notification-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[92vw] sm:w-96 p-0 overflow-hidden">
        <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-500" />
            <p className="text-sm font-semibold">Notifications</p>
            {unread > 0 && (
              <span className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full font-semibold">{unread} unread</span>
            )}
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              <Bell className="w-10 h-10 mx-auto opacity-30 mb-2" />
              No notifications yet
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.notification_id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-3 py-3 border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors flex gap-2.5 ${!n.read ? "bg-indigo-50/40 dark:bg-indigo-900/10" : ""}`}
              >
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${typeAccent(n.type)}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm leading-tight line-clamp-1 ${!n.read ? "font-semibold text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-300"}`}>
                    {n.title}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{formatRelative(n.created_at)}</p>
                </div>
              </button>
            ))
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem
          className="cursor-pointer justify-center text-indigo-600 dark:text-indigo-400 py-2.5 font-medium"
          onClick={() => { setOpen(false); navigate("/notifications"); }}
        >
          View all notifications →
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
